import { useCallback, useMemo } from 'react';
import { getMsalInstance } from '../Infrastructure/Security/msalInstance';
import { useAppInsightsContext } from '@microsoft/applicationinsights-react-js';
import { IExceptionTelemetry } from '@microsoft/applicationinsights-web';
import { useAppSelector } from '@reduxStore';
import { AppSettings } from '@/Infrastructure/Configuration/types';
import { SilentRequest } from '@azure/msal-browser';

/**
 * Wrapper around the useAccount hook provided by MSAL to simplify for consuming samAccountName etc.
 */
export default function useEntraAccount() {
  const instance = getMsalInstance();
  const activeAccount = instance.getActiveAccount();
  const appInsights = useAppInsightsContext();
  const { scope } = useAppSelector((state) => (state.appSettings as AppSettings).azure);

  const accessTokenRequest: SilentRequest = useMemo(
    () => ({
      scopes: [scope],
      account: activeAccount ?? undefined
    }),
    [activeAccount, scope]
  );

  /** Method for retreiving the access token from the msal instance */
  const getAccessToken = useCallback(async () => {
    return (await instance.acquireTokenSilent(accessTokenRequest)).accessToken;
  }, [accessTokenRequest, instance]);

  const samAccountName = useMemo<string | undefined>(() => {
    if (activeAccount && activeAccount.idTokenClaims) {
      return String(activeAccount.idTokenClaims['samAccountName']);
    }

    return undefined;
  }, [activeAccount]);

  /**
   * Method for logging the user out of the application and resetting application state.
   */
  const logout = useCallback(() => {
    // Log out of Entra
    instance
      .logout({
        account: activeAccount
      })
      .catch((error: unknown) => {
        const errorTelemetry = error as IExceptionTelemetry;
        appInsights.trackException(errorTelemetry);
      });
  }, [instance, appInsights, activeAccount]);

  return { activeAccount, samAccountName, logout, getAccessToken };
}
